import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { AUTO_OPEN_MENUS } from '../../config/menuConfig'
import { MenuItem } from '../../types/layout'

const collectMenuIds = (items: MenuItem[]): string[] => {
  const ids: string[] = []
  items.forEach((item) => {
    ids.push(item.id)
    if (item.submenu?.length) {
      ids.push(...collectMenuIds(item.submenu))
    }
  })
  return ids
}

const pathMatchesItem = (pathname: string, item: MenuItem): boolean => {
  if (pathname === item.path || pathname.startsWith(item.path)) {
    return true
  }
  return item.submenu?.some((sub) => pathMatchesItem(pathname, sub)) || false
}

/**
 * Hook para manejar el estado de apertura/cierre de los menús del sidebar
 * Auto-abre menús según la ruta actual (incluye submenús anidados)
 */
export const useMenuState = (menuItems: MenuItem[]) => {
  const location = useLocation()

  const allMenuIds = useMemo(() => collectMenuIds(menuItems), [menuItems])

  const initialMenuState = useMemo(() => {
    const state: Record<string, boolean> = {}
    const autoOpenIds = AUTO_OPEN_MENUS[location.pathname] || []

    allMenuIds.forEach((id) => {
      state[id] = autoOpenIds.includes(id)
    })

    menuItems.forEach((item) => {
      if (pathMatchesItem(location.pathname, item)) {
        state[item.id] = true
      }
      item.submenu?.forEach((sub) => {
        if (sub.submenu?.length && pathMatchesItem(location.pathname, sub)) {
          state[sub.id] = true
          state[item.id] = true
        }
      })
    })

    return state
  }, [allMenuIds, menuItems, location.pathname])

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(initialMenuState)

  useEffect(() => {
    setOpenMenus((prev) => {
      const newState: Record<string, boolean> = { ...prev }
      const autoOpenIds = AUTO_OPEN_MENUS[location.pathname] || []

      allMenuIds.forEach((id) => {
        if (autoOpenIds.includes(id)) {
          newState[id] = true
        }
      })

      menuItems.forEach((item) => {
        if (pathMatchesItem(location.pathname, item)) {
          newState[item.id] = true
        }
        item.submenu?.forEach((sub) => {
          if (sub.submenu?.length && pathMatchesItem(location.pathname, sub)) {
            newState[sub.id] = true
            newState[item.id] = true
          }
        })
      })

      return newState
    })
  }, [location.pathname, menuItems, allMenuIds])

  const toggleMenu = (menuId: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }))
  }

  const openMenu = (menuId: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: true,
    }))
  }

  const closeMenu = (menuId: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: false,
    }))
  }

  const isMenuOpen = (menuId: string) => openMenus[menuId] || false

  return {
    openMenus,
    toggleMenu,
    openMenu,
    closeMenu,
    isMenuOpen,
  }
}
